import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { buildDriverLineup, formatLineupMiles, lineupColumnTotals } from '../utils/driverLineup';
import { formatTripRevenue } from '../utils/tripRevenue';

const DriverShiftLineup = ({ drivers, multiloadRoutes = null }) => {
  const [open, setOpen] = useState(false);
  const rows = buildDriverLineup(drivers, multiloadRoutes);
  const totals = lineupColumnTotals(rows);
  const uniqueDrivers = new Set(rows.map((row) => String(row.driverId || '').split('#')[0])).size;
  const driverWord = uniqueDrivers === 1 ? 'driver' : 'drivers';
  const countLabel = uniqueDrivers === rows.length
    ? `${rows.length} ${rows.length === 1 ? 'driver' : 'drivers'}`
    : `${uniqueDrivers} ${driverWord} · ${rows.length} windows`;
  const columns = [
    { key: 'los', label: 'LOS / space type' },
    { key: 'name', label: 'Driver name' },
    { key: 'revenue', label: 'Revenue', total: totals.revenue },
    { key: 'start', label: 'Shift start' },
    { key: 'end', label: 'Shift end' },
    { key: 'shift', label: 'Shift', total: totals.shift },
    { key: 'working', label: 'Working shift', total: totals.working },
    { key: 'busy', label: 'Busy', total: totals.busy },
    { key: 'lm', label: 'LM', total: totals.loaded },
    { key: 'dh', label: 'DH', total: totals.deadhead },
    { key: 'away', label: 'Away', total: totals.away },
    { key: 'total', label: 'Total', total: totals.total },
  ];

  return (
    <section className="bg-white rounded-xl border border-card-border shadow-sm overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left hover:bg-main-base-50 transition-colors"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="flex items-center gap-2 min-w-0">
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-main-light transition-transform ${open ? '' : '-rotate-90'}`}
            aria-hidden="true"
          />
          <h3 className="text-sm font-semibold text-main-dark">Driver Lineup with Shifts</h3>
        </span>
        <span className="text-[10px] font-medium text-dashboard-stats-primary bg-dashboard-stats-primary-light px-2 py-0.5 rounded-full tabular-nums shrink-0">
          {countLabel}
        </span>
      </button>

      {open && (
        <div className="border-t border-card-border">
          {rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-main-light">No drivers in this view.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1280px] text-left text-xs text-main-dark">
                <thead>
                  <tr className="border-b border-card-border text-[10px] text-main-light">
                    {columns.map((column) => (
                      <th key={column.key} scope="col" className="px-3 py-2 font-medium whitespace-nowrap">
                        <span className="uppercase tracking-wide">{column.label}</span>
                        {column.total != null && (
                          <span className="ml-1 normal-case tracking-normal text-main-dark font-semibold tabular-nums">
                            ({column.total})
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={`${row.driverId || row.driverName}-${index}`} className="border-b border-card-border last:border-b-0">
                      {row.losRowSpan > 0 && (
                        <td
                          rowSpan={row.losRowSpan}
                          className="px-3 py-2 align-middle font-medium border-r border-card-border bg-main-base-50 whitespace-nowrap"
                        >
                          {row.losLabel}
                        </td>
                      )}
                      <td className="px-3 py-2 whitespace-nowrap">{row.driverName}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap font-medium">{formatTripRevenue(row.revenue)}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{row.shiftStart}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{row.shiftEnd}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{row.shiftDuration}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{row.workingShift}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{row.busyShift}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{formatLineupMiles(row.loaded)}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{formatLineupMiles(row.deadhead)}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">{formatLineupMiles(row.away)}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap font-medium">{formatLineupMiles(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default DriverShiftLineup;
